import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import { getAuthOptions } from '../../services/auth-setup/auth-setup-service';
import type { AppState } from '../../store/state/app.state';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import UpdateSecurityModal from '../login/UpdateSecurityModal';

function AuthOptions() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userData = useSelector((state: AppState) => state.UserData);

  const [visibleMethods, setVisibleMethods] = useState<{
    TOTP: boolean;
    MESSAGE_OTP: boolean;
  } | null>(null);

  const [isShowUpdateSecurityModal, setIsShowUpdateSecurityModal] = useState<boolean>(false);
  const [isUseDifferentMethod, setIsUseDifferentMethod] = useState<boolean>(false);
  const [isSameEmail, setIsSameEmail] = useState<boolean>(false);
  const isPassKeyFailed = localStorage.getItem(Common.IS_PASSKEY_FAILED) === 'true';
  const [isChangeAuthMethod, setIsChangeAuthMethod] = useState<boolean>(false);

  useEffect(() => {
    const handlePopState = () => {
      navigate('/login', { replace: true });
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    const storedData = localStorage.getItem(Common.RESET_AUTH_METHOD);
    const { isUseDifferentMethod, email } = storedData
      ? JSON.parse(storedData)
      : { isUseDifferentMethod: false, email: '' };

    setIsUseDifferentMethod(Boolean(isUseDifferentMethod));
    setIsSameEmail(email === userData.email);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    getAuthOption();
    setIsChangeAuthMethod(sessionStorage.getItem(Common.CHANGE_AUTH_METHOD) === 'true');
  }, []);

  const getAuthOption = async () => {
    try {
      const response = await getAuthOptions();
      const data = response.Data.driver2FAStatus;

      const totpEnabled = data.some((method) => method.code === 'TOTP');
      const messageOTPEnabled = data.some((method) => method.code === 'MESSAGE_OTP');

      setIsShowUpdateSecurityModal(localStorage.getItem(Common.SHOW_UPDATE_MFA_MODAL) === 'true');

      setVisibleMethods({
        TOTP: totpEnabled,
        MESSAGE_OTP: messageOTPEnabled,
      });
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.toString());
      }
    }
  };

  const handleSubmit = () => {
    localStorage.removeItem(Common.SHOW_UPDATE_MFA_MODAL);
    setIsShowUpdateSecurityModal(false);
  };

  if (!visibleMethods) {
    return <div className="spinner"></div>;
  }

  return (
    <>
      <Helmet>
        <title>{companyNameReplace(t('authentication.title'))} </title>
      </Helmet>
      <div className="auth-options-wrapper">
        <div className="auth-options-container">
          <div className="auth-header">
            <div className="auth-icon-container">
              <img src={images.authIconSVG} alt="Security" className="auth-icon-image" />
            </div>
            {isChangeAuthMethod ? (
              <>
                <h1 className="auth-title">{t('security.selectAuthMethod.header')}</h1>
                <p className="auth-subtitle">{t('security.selectAuthMethod.subHeader')}</p>
              </>
            ) : (
              <>
                <h1 className="auth-title">{t('authentication.authOptions.header')}</h1>
                <p className="auth-subtitle">{t('authentication.authOptions.subHeader')}</p>
              </>
            )}

            {((isUseDifferentMethod && isSameEmail) || isPassKeyFailed) && (
              <p className="auth-failed-message">{t('authentication.authOptions.methodFailedMessage')}</p>
            )}
          </div>

          <div className="auth-options-grid">
            {visibleMethods.MESSAGE_OTP && (
              <div
                className="auth-option-card"
                onClick={() => navigate(Common.Routes.AUTH.SETUP_PHONE_NUMBER, { replace: true })}
              >
                <div className="auth-option-icon">
                  <img src={images.whatsAppIconSVG} alt="Phone" />
                </div>
                <div className="auth-option-content">
                  <h3 className="auth-option-title">{t('authentication.authOptionTitle.phoneNumber')}</h3>
                  <p className="auth-option-description">{t('authentication.authOptionDescription.phoneNumber')}</p>
                </div>
              </div>
            )}

            {visibleMethods.TOTP && (
              <div
                className="auth-option-card"
                onClick={() => navigate('/auth/setup/authenticator', { replace: true })}
              >
                <div className="auth-option-icon">
                  <img src={images.authAppIconSVG} alt="Authenticator" />
                </div>
                <div className="auth-option-content">
                  <h3 className="auth-option-title">{t('authentication.authOptionTitle.authenticatorApp')}</h3>
                  <p className="auth-option-description">
                    {t('authentication.authOptionDescription.authenticatorApp')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isShowUpdateSecurityModal && (
        <UpdateSecurityModal
          isShowUpdateSecurityModal={isShowUpdateSecurityModal}
          handleSubmit={handleSubmit}
          showBackButton={false}
        />
      )}
    </>
  );
}

export default AuthOptions;
